import { memo, useMemo, useState } from "react";
import { Star, ShieldCheck, AlertTriangle, Clock, Link2, ChevronDown } from "lucide-react";
import { scoreSources, reliabilityOverview, type SourceReliability } from "@/lib/intel/reliability";

function Stars({ n }: { n: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${n} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3 w-3 ${i <= n ? "text-amber" : "text-muted-foreground/30"}`}
          fill={i <= n ? "currentColor" : "none"}
        />
      ))}
    </span>
  );
}

const AXIS_ICON = [ShieldCheck, Clock, Link2, AlertTriangle];

function Row({ r }: { r: SourceReliability }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded border border-panel-border bg-background/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 p-2.5 text-left hover:bg-panel/40"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Stars n={r.stars} />
            <span className="truncate text-sm font-medium">{r.source}</span>
            {r.contradictions.length > 0 && (
              <span className="rounded bg-danger/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-danger">
                conflict
              </span>
            )}
            {r.corroboratedBy.length === 0 && (
              <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-amber">
                single source
              </span>
            )}
          </div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {r.classLabel}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{r.verdict}</div>
        </div>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="grid gap-2 border-t border-panel-border p-2.5 sm:grid-cols-2">
          {r.axes.map((a, i) => {
            const Icon = AXIS_ICON[i] ?? ShieldCheck;
            const tone = a.delta > 0 ? "text-success" : a.delta < 0 ? "text-danger" : "text-muted-foreground";
            return (
              <div key={a.label} className="rounded border border-panel-border/60 bg-panel/30 p-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-cyan">
                    <Icon className="h-3 w-3" /> {a.label}
                  </span>
                  <span className={`font-mono text-[10px] ${tone}`}>
                    {a.delta > 0 ? "+" : ""}{a.delta.toFixed(1)}★
                  </span>
                </div>
                <div className="mt-1 text-xs font-medium">{a.value}</div>
                <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{a.detail}</div>
              </div>
            );
          })}
          {r.summary && (
            <div className="sm:col-span-2 rounded border border-panel-border/60 bg-background/50 p-2 font-mono text-[10px] text-muted-foreground">
              {r.summary.slice(0, 400)}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function SourceReliabilityPanelImpl({
  collectors,
  compact = false,
}: {
  collectors: Array<{ source: string; status?: string; summary?: string; latencyMs?: number; data?: unknown }> | undefined;
  compact?: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const rows = useMemo(() => scoreSources(collectors ?? []), [collectors]);
  const stats = useMemo(() => reliabilityOverview(rows), [rows]);

  if (!rows.length) return null;
  const visible = showAll ? rows : rows.slice(0, compact ? 5 : 12);

  return (
    <section className="glass rounded-xl p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Source reliability</div>
        <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">avg <Stars n={Math.round(stats.avgStars)} /> {stats.avgStars.toFixed(1)}</span>
          <span className="text-success">{stats.highTrust} high-trust</span>
          <span className="text-danger">{stats.contradicted} contradicted</span>
          <span className="text-amber">{stats.uncorroborated} uncorroborated</span>
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Every collector is rated 1–5 stars from its class baseline, then adjusted for recency, corroboration
        across other sources and detected contradictions. Open a row for the reasoning.
      </p>
      <ul className="mt-3 space-y-1.5">
        {visible.map((r) => <Row key={r.source} r={r} />)}
      </ul>
      {rows.length > visible.length && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-3 w-full rounded-md border border-panel-border bg-panel/40 py-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
        >
          Show all {rows.length} sources
        </button>
      )}
    </section>
  );
}

/** Memoised: these panels render large evidence sets and must not re-render on every keystroke. */
export const SourceReliabilityPanel = memo(SourceReliabilityPanelImpl);
